import React, { useState, useRef, useEffect } from 'react';
import { Share2, Copy, Check, Mail, MessageCircle, Facebook, Twitter } from 'lucide-react';
import { useT } from '../lib/i18n';

/**
 * Reusable share control. `url` should be the crawler-friendly /api/share link
 * so that WhatsApp / Facebook / iMessage render a rich preview of the piece.
 */
export const ShareMenu = ({ url, title, variant = 'button', className = '' }) => {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const enc = encodeURIComponent;
  const text = title || 'The Chimera Gallery';
  const links = {
    whatsapp: `https://wa.me/?text=${enc(text + ' ' + url)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
    x: `https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(text)}`,
    email: `mailto:?subject=${enc(text)}&body=${enc(url)}`,
  };

  const copy = async (e) => {
    e?.stopPropagation();
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) { /* ignore */ }
  };

  const onTrigger = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (navigator.share) {
      try { await navigator.share({ title: text, url }); return; } catch (_) { /* fall through to menu */ }
    }
    setOpen((o) => !o);
  };

  const stop = (e) => e.stopPropagation();

  const triggerCls = variant === 'icon'
    ? 'inline-flex items-center justify-center w-10 h-10 border border-dark-green/30 text-dark-green hover:border-dark-crimson hover:text-dark-crimson bg-ivory/90 transition-colors'
    : 'inline-flex items-center gap-2 px-4 py-4 text-dark-green hover:text-dark-crimson border border-dark-green/30 hover:border-dark-crimson transition-colors';

  const item = 'flex items-center gap-3 w-full px-4 py-2.5 text-sm text-dark-green hover:bg-gold/10 transition-colors';

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button type="button" onClick={onTrigger} className={triggerCls} data-testid="share-menu-trigger" aria-label={t('share.label')}>
        <Share2 size={16} />
        {variant !== 'icon' && <span className="text-sm tracking-wider">{copied ? t('share.copied') : t('share.label')}</span>}
      </button>

      {open && (
        <div
          onClick={stop}
          className="absolute right-0 mt-2 w-52 bg-ivory border-2 border-gold shadow-2xl z-50"
          style={{ fontFamily: 'Cormorant Garamond, serif' }}
          data-testid="share-menu"
        >
          <button className={item} onClick={copy} data-testid="share-copy">
            {copied ? <Check size={16} className="text-rich-green" /> : <Copy size={16} />}
            {copied ? t('share.copied') : t('share.copy')}
          </button>
          <a className={item} href={links.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="share-whatsapp" onClick={stop}>
            <MessageCircle size={16} /> WhatsApp
          </a>
          <a className={item} href={links.facebook} target="_blank" rel="noopener noreferrer" data-testid="share-facebook" onClick={stop}>
            <Facebook size={16} /> Facebook
          </a>
          <a className={item} href={links.x} target="_blank" rel="noopener noreferrer" data-testid="share-x" onClick={stop}>
            <Twitter size={16} /> X
          </a>
          <a className={item} href={links.email} data-testid="share-email" onClick={stop}>
            <Mail size={16} /> {t('share.email')}
          </a>
        </div>
      )}
    </div>
  );
};

export default ShareMenu;
